import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  Box,
  TextField,
  Chip,
  FormControlLabel,
  Switch,
  DialogTitle,
  DialogContent,
  InputAdornment,
  DialogActions,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";
import GenericInfiniteTable, { ColumnDef } from "./GenericInfiniteTable";
import { PatientTableDto, PatientTableFilters } from "../../api-client";
import { useInfinitePatientsTable } from "./hooks/usePatientTable";
import { useLocationId } from "../../stores/User/User.hooks";
import Controls from "../../atoms/Controls";
import { useMasterTables } from "../../stores/MasterTables";
import { observer } from "mobx-react";
import useStore from "../../helpers/useStore";
import { NavTablesContext } from "../../stores/NavTables";

interface PatientTableDialogProps {
  open: boolean;
  onClose: () => void;
  onPatientSelect: (patientId: number) => void;
}

const PatientTableDialog = observer(
  ({ open, onClose, onPatientSelect }: PatientTableDialogProps) => {
    const locationId = useLocationId();
    const { data: masterTablesData } = useMasterTables();
    const NavTableStore = useStore(NavTablesContext);
    const {
      patientNavTableParams,
      setPatientTableFilters: setFilters,
      patientNavTableSelectedIndex: selectedIndex,
      setPatientNavTableSelectedIndex: setSelectedIndex,
    } = NavTableStore;
    const filters = patientNavTableParams.patientTableFilters;

    const locationsOptions =
      (masterTablesData || [])
        .filter((obj: any) => "location" in obj)
        .map((obj: any) =>
          obj.location?.map((l: any) => ({
            label: l.name?.toString(),
            value: l.id?.toString(),
          }))
        )?.[0] || [];

    const handlePatientSelect = (patient: PatientTableDto) => {
      onPatientSelect(patient?.id as number);
    };

    const handleFilterChange = (field: keyof PatientTableFilters, value: any) => {
      setFilters((prev) => ({
        ...prev,
        [field]: value !== "" ? value : undefined,
      }));
    };

    const handleSearchStringChange = (newString: string | ((prevString: string) => string)) => {
      setFilters((prev) => ({
        ...prev,
        lastName: typeof newString === "function" ? newString(prev.lastName || "") : newString,
      }));
    }

    useEffect(() => {
      if (locationId) {
        setFilters((prev) => ({ ...prev, location: locationId }));
      }
    }, [locationId]);

   // ---- Memoized filters ----
    const memoizedFilters = useMemo(() => ({ ...filters }), [filters]);


    // ---- INFINITE QUERY ----
    const {
      data,
      isFetchingNextPage,
      isLoading,
      hasNextPage: hasMoreData,
      fetchNextPage,
    } = useInfinitePatientsTable({
      filters: memoizedFilters,
      enabled: open && !!locationId,
    });

    const patients: PatientTableDto[] = data?.pages.flat() ?? [];

    // ---- TABLE COLUMNS ----
    const columns: ColumnDef<PatientTableDto>[] = useMemo(
      () => [
        {
          key: "id",
          header: "ID",
          render: (p) => p.id,
          align: "center",
        },
        {
          key: "patientName",
          header: "Name",
          render: (p) => `${p.lastName}, ${p.firstName}`,
          align: "left",
          sortable: true,
        },
        {
          key: "dob",
          header: "DOB",
          render: (p) => (p.dob ? format(new Date(p.dob), "dd.MM.yyyy") : "-"),
        },
        { key: "payer", header: "Versicherungsgesellschaft", render: (p) => p.payer?.name ?? "-" },
        { key: "coPayment", header: "Zuzahlung", render: (p) => (p.coPayment ? "Ja" : "Nein") },
        { key: "insuranceNo", header: "Versicherungsnummer", render: (p) => p.insuranceNo ?? "-" },
        {
          key: "location",
          header: "Standort",
          render: (p) => p.location?.name ?? "-",
        },
        {
          key: "address",
          header: "Adresse",
          render: (p) => {
            const primary = p.addresses[0]?.isPrimary ? p.addresses[0] : null;
            if (!primary) return "-";

            const { addressLine1, addressLine2, city, phone1 } = primary.contactInfo ?? {};

            const displayText = `${addressLine1 ?? "-"}, ${addressLine2 ?? "-"} ${city ?? "-"} ${
              phone1 ?? "-"
            }`;

            return (
              <Tooltip
                title={
                  <Typography sx={{ whiteSpace: "pre-line" }}>
                    {`${addressLine1 ?? "-"}\n${addressLine2 ?? "-"}\n${city ?? "-"}\n${
                      phone1 ?? "-"
                    }`}
                  </Typography>
                }
                arrow
              >
                <span>{displayText}</span>
              </Tooltip>
            );
          },
        },
      ],
      []
    );

      const handleSortChange = (columnKey: string) => {
    setFilters((prev) => {
      type SortByType = Partial<Record<string, "ASC" | "DESC">>;
      const currentSort: SortByType = prev.sortBy || {};
      const currentDirection = currentSort[columnKey as string];

      const newDirection =
        currentDirection === "ASC"
          ? "DESC"
          : currentDirection === "DESC"
          ? undefined
          : "ASC";

      const updatedSort: SortByType = { ...currentSort };

      if (!newDirection) {
        delete updatedSort[columnKey as string];
      } else {
        updatedSort[columnKey as string] = newDirection;
      }

      return {
        ...prev,
        sortBy: updatedSort,
      };
    });
  };
  

    // ---- TOOLBAR ----
    const toolbar = (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        {/* Location Filter */}
        <Controls.Select
          name="location"
          label={<Typography sx={{ fontSize: "1rem" }}>Standort</Typography>}
          value={filters.location ?? ""}
          onChange={(e: any) => handleFilterChange("location", e.target.value)}
          options={locationsOptions}
          style={{ width: 200 }}
        />

        {/* Last Name Search */}
        <TextField
          label={<Typography sx={{ fontSize: "1rem" }}>Nachname</Typography>}
          variant="outlined"
          size="small"
          value={filters.lastName ?? ""}
          onChange={(e) => handleSearchStringChange( e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Show Deleted Switch */}
        <FormControlLabel
          control={
            <Switch
              checked={filters.includeDeleted ?? false}
              onChange={(e) => handleFilterChange("includeDeleted", e.target.checked)}
              color="primary"
            />
          }
          label={<Typography sx={{ fontSize: "1rem" }}>Gelöschte anzeigen</Typography>}
        />
      </Box>
    );

    // ---- DIALOG ----
    return (
      <Dialog fullScreen open={open} onClose={onClose}>
        <DialogTitle sx={{ p: 2, fontSize: "1.5rem" }}>Patienten</DialogTitle>
        <DialogContent>
          <GenericInfiniteTable
            data={patients}
            columns={columns}
            hasMoreData={hasMoreData}
            fetchMoreData={fetchNextPage}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            onRowClick={handlePatientSelect}
            toolbar={toolbar}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            onSearchStringChange={handleSearchStringChange}
            onSortChange={handleSortChange}
            sortBy={filters.sortBy as any} // now an object
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="text" onClick={onClose}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);
export default PatientTableDialog;
